import React, { useState } from 'react';
import { X, ChevronDown, ChevronUp } from 'lucide-react';
import { useQueryClient } from '@tanstack/react-query';
import { Channel } from '@meradio/shared-types';
import { useAppDispatch, useAppSelector } from '@/store/store';
import { followChannel, selectFollowedChannels } from '@/features/channels/channelsSlice';
import { queryKeys } from '@/lib/queryClient';
import { useDismissSuggestion } from '@/hooks/useSuggestionsQuery';
import type { ChannelSuggestion, SuggestionSource } from '@/hooks/useSuggestionsQuery';
import { Button, IconButton, YouTubeChannelLink } from '@/components/ui';
import ChannelPreview from '@/components/ChannelPreview';

const SOURCE_WEIGHTS: Record<SuggestionSource['type'], number> = {
  featured: 3,
  library_label: 3,
  playlist: 2,
  library_artist: 2,
  mb_related: 1,
};

function primarySource(sources: SuggestionSource[]): SuggestionSource {
  return [...sources].sort((a, b) => SOURCE_WEIGHTS[b.type] - SOURCE_WEIGHTS[a.type])[0];
}

interface SuggestionCardProps {
  suggestion: ChannelSuggestion;
}

const SuggestionCard: React.FC<SuggestionCardProps> = ({ suggestion }) => {
  const dispatch = useAppDispatch();
  const queryClient = useQueryClient();
  const followedChannels = useAppSelector(selectFollowedChannels);
  const dismissSuggestion = useDismissSuggestion();
  const [isFollowing, setIsFollowing] = useState(false);
  const [expanded, setExpanded] = useState(false);

  const source = primarySource(suggestion.sources);

  const attribution = (() => {
    switch (source.type) {
      case 'featured': {
        const via = followedChannels.find((channel) => channel.channelId === source.via);
        return `Featured by ${via ? via.name : 'a channel you follow'}`;
      }
      case 'playlist':
        return 'Playlisted by a channel you follow';
      case 'library_label':
        return `Label behind ${source.detail ?? 'your liked tracks'}`;
      case 'library_artist':
        return `Artist in ${source.detail ?? 'your liked tracks'}`;
      case 'mb_related':
        return `Related to ${source.via}`;
      default:
        return '';
    }
  })();

  const handleFollow = async () => {
    setIsFollowing(true);
    try {
      const channel: Channel = {
        id: suggestion.suggestedChannelId,
        channelId: suggestion.suggestedChannelId,
        name: suggestion.name,
        description: suggestion.description,
        thumbnailUrl: suggestion.thumbnailUrl,
        createdAt: new Date(),
        updatedAt: new Date(),
      };
      await dispatch(followChannel(channel)).unwrap();
      queryClient.invalidateQueries({ queryKey: queryKeys.suggestions });
    } catch (error) {
      console.error('Failed to follow suggested channel', error);
    } finally {
      setIsFollowing(false);
    }
  };

  const handleDismiss = () => {
    dismissSuggestion.mutate(suggestion.suggestedChannelId);
  };

  return (
    <div className="relative flex-shrink-0 w-56 bg-white dark:bg-bg-dark-secondary border border-meradio-light dark:border-border-dark rounded-xl shadow-sm hover:shadow-lg hover:border-meradio-primary dark:hover:border-meradio-primary transition-all duration-200 p-4">
      <IconButton
        aria-label={`Dismiss ${suggestion.name}`}
        onClick={handleDismiss}
        disabled={dismissSuggestion.isPending}
        size="sm"
        className="absolute top-2 right-2 text-meradio-dark-light dark:text-text-dark-secondary"
      >
        <X className="w-4 h-4" />
      </IconButton>

      <div className="w-16 h-16 mx-auto mb-3">
        {suggestion.thumbnailUrl ? (
          <img
            src={suggestion.thumbnailUrl}
            alt={`${suggestion.name} thumbnail`}
            className="w-full h-full rounded-full object-cover border-2 border-meradio-light"
            loading="lazy"
            onError={(e) => {
              const target = e.target as HTMLImageElement;
              target.src = `https://ui-avatars.com/api/?name=${encodeURIComponent(suggestion.name)}&size=80&background=6C5CE7&color=ffffff&format=png&rounded=true`;
            }}
          />
        ) : (
          <div className="w-full h-full rounded-full bg-gradient-to-br from-meradio-primary to-meradio-secondary flex items-center justify-center">
            <span className="text-white font-bold text-lg">
              {suggestion.name.charAt(0).toUpperCase()}
            </span>
          </div>
        )}
      </div>

      <div className="flex items-center justify-center gap-1">
        <h3 className="font-semibold text-meradio-dark dark:text-text-dark-primary text-sm truncate min-w-0">
          {suggestion.name}
        </h3>
        <YouTubeChannelLink
          channelId={suggestion.suggestedChannelId}
          name={suggestion.name}
          className="flex-shrink-0 !w-6 !h-6 text-meradio-dark-light dark:text-text-dark-secondary"
        />
      </div>
      <p className="text-xs text-meradio-dark-light dark:text-text-dark-secondary text-center mt-1 mb-3 line-clamp-2">
        {attribution}
      </p>

      <div className="flex items-center gap-2">
        <Button
          onClick={handleFollow}
          disabled={isFollowing}
          isLoading={isFollowing}
          variant="primary"
          size="sm"
          fullWidth
          aria-label={`Follow ${suggestion.name}`}
        >
          {isFollowing ? 'Following...' : '+ Follow'}
        </Button>

        <Button
          onClick={() => setExpanded((prev) => !prev)}
          variant="outline"
          size="sm"
          aria-label={expanded ? `Hide preview for ${suggestion.name}` : `Preview ${suggestion.name}`}
          aria-expanded={expanded}
          className="flex-shrink-0 !px-2"
        >
          {expanded ? (
            <>
              Hide
              <ChevronUp className="w-3.5 h-3.5" />
            </>
          ) : (
            <>
              Preview
              <ChevronDown className="w-3.5 h-3.5" />
            </>
          )}
        </Button>
      </div>

      {expanded && (
        <div className="mt-3 pt-3 border-t border-meradio-light dark:border-border-dark">
          <ChannelPreview
            channelId={suggestion.suggestedChannelId}
            channelName={suggestion.name}
            enabled={expanded}
          />
        </div>
      )}
    </div>
  );
};

export default SuggestionCard;
